import { Component, OnInit, Input, Output, EventEmitter, OnDestroy } from '@angular/core';
import { FormGroup } from "@angular/forms";
import { Observable, of, BehaviorSubject, Subscription } from "rxjs";
import { tap } from "rxjs/operators";
import { PaymentService, AuthorizationRequest, AccountInformation, BillingAddressInformation, PaymentInformation } from "../../../payments/services/payment.service";
import { PaymentCalculatorService } from "../../../payments/services/payment-calculator.service";
import { PaymentItem, ApplicationPayment, PaymentStatus, AuthorizationResponse } from "../../../payments/models/paymentItem.model";
import { PaymentSteps, ApplicationPaymentParameters, ApplicationType } from "../../models/applications.models";
import { DynamicDialogService } from "../../../core/services/dynamic-dialog.service";
import { Dialog, DialogButton } from "../../../core/models/dialog.model";
import { DocumentConfig } from "../../../services/application-config.service";


@Component({
    selector: 'app-payments',
    templateUrl: './payments.component.html',
    styleUrls: ['./payments.component.scss'],
    standalone: false
})
export class PaymentsComponent implements OnInit, OnDestroy {

    private _active: boolean = false;

    get active(): boolean {
        return this._active;
    }

    @Input() set active(value: boolean) {
        this._active = value;       
    }

    private _authorization: AuthorizationResponse;

    get authorization(): AuthorizationResponse {
        return this._authorization;
    }


    @Input() set authorization(value: AuthorizationResponse) {
        this._authorization = value;
    }

    @Output() authorizationRequested: EventEmitter<AuthorizationRequest> = new EventEmitter<AuthorizationRequest>();
    @Output() authorizationResponseReceived: EventEmitter<AuthorizationResponse> = new EventEmitter<AuthorizationResponse>();
    @Output() receiptIssued: EventEmitter<boolean> = new EventEmitter<boolean>();

    calculatingPayments: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    @Output() checkPaymentSelected: EventEmitter<ApplicationPayment[]> = new EventEmitter<ApplicationPayment[]>();

	@Input() config: DocumentConfig;
	@Input() allowExpedite: boolean = false;

  

    confirmAuthorizationDialog: Dialog = new Dialog("You have requested electronic payment.  Your progress will be saved and you will be redirected to another site to process your payment authorization.  Do you wish to proceed?", [new DialogButton("NO", false, () => { return false; }), new DialogButton("Yes", true, () => { this.submitToCBOSS(); })])

    get currentStep(): PaymentSteps {
        if (this.form == null) return PaymentSteps.Itemization;
        let ctrl = this.form.get('step');
        if (ctrl == null) return PaymentSteps.Itemization;
        return ctrl.value;
    }

    set currentStep(value: PaymentSteps) {
        if (this.form == null) return;
        let ctrl = this.form.get('step');
        if (ctrl == null) return;
        ctrl.setValue(value);
        if (value == PaymentSteps.PaymentType) {
            let typeCtrl = this.form.get('paymentType');
            if (typeCtrl != null) typeCtrl.enable();
        }
    }

    get existingDocumentId(): any {
        if (this.form == null) return null;
        let ctrl = this.form.get('invoice');
        if (ctrl == null) return null;
        return ctrl.value;
    }


    get fees(): ApplicationPayment[] {
        if (this.form == null) return [];
        let ctrl = this.form.get('payments');
        if (ctrl == null) return [];
        return ctrl.value;
    }

    @Input() set fees(value: ApplicationPayment[]) {
        if (this.form != null) {
            let ctrl = this.form.get('payments');
            if (ctrl != null) ctrl.setValue(value);
        }
    }

    get isCalculating(): boolean {
        return this.calculatingPayments.value;
    }

    private _parameters: ApplicationPaymentParameters;

    private _form: FormGroup = new FormGroup({});

    get form(): FormGroup {
        return this._form;
    }

    @Input() set form(value: FormGroup) {
        this._form = value;
    }


    @Input() organizationId: string;

    @Input() organizationName: string;

    get parameters(): ApplicationPaymentParameters {
        return this._parameters;
    }

    @Input() set parameters(value: ApplicationPaymentParameters) {
        this._parameters = value;
        if (this.active && this._parameters != null) this.calculatePayments(this._parameters.forceRecalculate);
    }

    private _payments: PaymentItem[];

    get paymentTypes(): any[] {
        return this.paymentService.paymentTypes;
    }

    

    get selectedPayment(): string {
        if (this.form == null) return null;
        let typeCtrl = this.form.get('paymentType');
        if (typeCtrl == null) return null;
        return typeCtrl.value;
    }

    get showInvoice(): boolean {
        let show: boolean = this.active && this.form.get('step').value == PaymentSteps.Invoicing && this.form.get('payments').value != null && this.form.get('payments').value.length > 0;
        return show;
    }

    steps = PaymentSteps;

    subscription: Subscription = new Subscription();


    constructor(private paymentService: PaymentService, private calculator: PaymentCalculatorService, private dialog: DynamicDialogService) { }

    ngOnInit() {
    }

    ngOnDestroy() {
        if (this.subscription != null) this.subscription.unsubscribe();
    }

    private buildAuthorizationRequest(): AuthorizationRequest {
        let accountInformation: AccountInformation = new AccountInformation();
        accountInformation.paymentType = this.selectedPayment;
        let billingInformation: BillingAddressInformation = new BillingAddressInformation();
        let paymentInformation = new PaymentInformation();
        paymentInformation.paymentItems = this._payments;
        let req = new AuthorizationRequest(accountInformation, billingInformation, paymentInformation);
        req.applicationNumber = this.parameters.applicationNumber;
        req.applicationType = this.parameters.applicationType;
        return req;
    }

    calculatePayments(forceRecalculate: boolean = false): void {
        if (this.fees == null || this.fees.length == 0 || forceRecalculate) {
            let obs$: Observable<PaymentItem[]> = of([]);
            if (this.parameters != null) {
                if (this.parameters.applicationType == ApplicationType.Stratigraphic) {
					obs$ = this.calculator.calculateStratigraphicWellFees(this.parameters, this.allowExpedite);
                }
            }
            this.calculatingPayments.next(true);
            let subs$: Subscription = obs$.pipe(
                tap((items: PaymentItem[]) => {
                    let paymentsChanged: boolean = false;
                    if (this.fees == null || this.fees.length == 0) {
                        paymentsChanged = true;
                        this.fees = this.convertPaymentItems(items);
                    } else {
                        let converted = this.convertPaymentItems(items);
                        converted.forEach(c => {
                            let match = this.fees.find(f => f.fee == c.fee && f.feeType == c.feeType);
                            if (match == null) paymentsChanged = true;
                        });
                        if (paymentsChanged) {
                            this.fees = converted;
                            this.form.get('invoice').setValue(null);
                        } else {
                            converted.forEach(c => {
                                if (c.paymentRecords != null && c.paymentRecords.length > 0) c.queuedReceipt = this.form.get('invoice').value;
                                else c.queuedInvoice = this.form.get('invoice').value;
                                let match = this.fees.find(f => f.fee == c.fee && f.feeType == c.feeType);
                                if (match != null) {
                                    c.id = match.id;
                                    c.workflowFieldId = match.workflowFieldId;
                                }
                            });
                            this.fees = converted;
                        }
                    }

                    this._payments = items;
                })
            ).subscribe(
                () => {
                    this.calculatingPayments.next(false);
                    this.currentStep = (this.form.get('paymentType').value == null) ? PaymentSteps.PaymentType : PaymentSteps.Invoicing;
                },
                (error) => {
                    console.error(error);
                    this.dialog.showError();
                }
            );
            this.subscription.add(subs$);
        }
       
    }

    confirmAuthorization(): void {
        this.confirmAuthorizationDialog.toggleDialog(true);
        this.dialog.updateDialog(this.confirmAuthorizationDialog);
    }

    convertPaymentItems(items: PaymentItem[]): ApplicationPayment[] {
        let payments: ApplicationPayment[] = [];
        items.forEach(i => {
            let pmt: ApplicationPayment = {
                id: i.id,
                accountDescription: i.accountDescription,
                description: i.description,
                organizationId: this.organizationId,
                organization: this.organizationName,
                fee: i.fee,
                feeType: i.description,
                applicationType: this.parameters.applicationType,
                applicationNumber: this.parameters.applicationNumber,
                applicationDate: new Date(),
                orderNumber: null,
                department: i.department,
                workflowId: this.parameters.applicationId.toString(),
                paymentStatus: PaymentStatus.Pending,
                ali: i.ali,
                fund: i.fund,
                program: i.program,
                revenueSourceCode: i.revenueSourceCode,
                reporting: i.reporting
            } as ApplicationPayment;
            payments.push(pmt);
        });

        return payments;
    }

    onReceiptIssued(payments: ApplicationPayment[]): void {
        this.fees = payments;
        let bounce: boolean = false;
        let withInvoice = this.fees.filter(f => f.queuedInvoice != null);
        if (withInvoice.length > 0) {
            bounce = this.form.get('invoice').value == null || this.form.get('invoice').value == "";
            this.form.get('invoice').setValue(withInvoice[0].queuedInvoice);
        } else {
            let withReceipt = this.fees.filter(f => f.queuedReceipt != null);
            if (withReceipt.length > 0) {
                bounce = this.form.get('invoice').value == null || this.form.get('invoice').value == "";
                this.form.get('invoice').setValue(withReceipt[0].queuedReceipt);
            }
        }
        this.receiptIssued.emit(bounce);
    }

    saveNewResponse(response: AuthorizationResponse): void {
        response.DateAuthorized = response.DateAuthorized.replace(/\+/g, ' ')
        this.authorization = response;
        this.authorizationResponseReceived.emit(response);
    }

    setParameters(params: ApplicationPaymentParameters): void {
        this.parameters = params;
    }

    submitPaymentType(): void {
        if (this.form.get('paymentType').value == 'LocalCheck') {
            this.checkPaymentSelected.emit(this.fees);
        } else {
            this.confirmAuthorization();
        }
    }

    submitToCBOSS(): void {
        this.authorizationRequested.emit(this.buildAuthorizationRequest());
    }
}


